// =============================================================================
// Origin UI — src/services/requestApi.ts
// Typed API functions for request, approval, task, and reference data endpoints.
// All functions use the centralized apiService for auth + error handling.
// =============================================================================

import { api } from '@/services/apiService';
import type {
  RequestListItem,
  RequestDetail,
  CreateRequestInput,
  UpdateRequestInput,
  AddCommentInput,
  CancelRequestInput,
  DepartmentRef,
  OfficeLocationRef,
  ManagerRef,
  RequestEmployeeRef,
  ITStaffRef,
  Comment,
  TaskListItem,
  TaskDetail,
} from '@/types/api';

// ─── Requests ───

export const requestApi = {
  list: () => api.get<RequestListItem[]>('/api/requests'),

  getById: (id: string) => api.get<RequestDetail>(`/api/requests/${id}`),

  create: (data: CreateRequestInput) =>
    api.post<RequestDetail>('/api/requests', data),

  update: (id: string, data: UpdateRequestInput) =>
    api.put<RequestDetail>(`/api/requests/${id}`, data),

  submit: (id: string) =>
    api.post<RequestDetail>(`/api/requests/${id}/submit`),

  resubmit: (id: string) =>
    api.post<RequestDetail>(`/api/requests/${id}/resubmit`),

  cancel: (id: string, data?: CancelRequestInput) =>
    api.post<RequestDetail>(`/api/requests/${id}/cancel`, data),

  addComment: (id: string, data: AddCommentInput) =>
    api.post<Comment>(`/api/requests/${id}/comments`, data),

  getTasks: (id: string) =>
    api.get<TaskListItem[]>(`/api/requests/${id}/tasks`),
};

// ─── Reference Data ───

export const referenceApi = {
  departments: () => api.get<DepartmentRef[]>('/api/reference/departments'),
  officeLocations: () => api.get<OfficeLocationRef[]>('/api/reference/office-locations'),
  managers: () => api.get<ManagerRef[]>('/api/reference/managers'),
  employees: () => api.get<RequestEmployeeRef[]>('/api/reference/employees'),
  itStaff: () => api.get<ITStaffRef[]>('/api/reference/it-staff'),
};

// ─── Approvals ───

export const approvalApi = {
  inbox: () => api.get<RequestListItem[]>('/api/approvals/inbox'),

  approve: (id: string, comment?: string) =>
    api.post<RequestDetail>(`/api/requests/${id}/approve`, { comment }),

  returnForChanges: (id: string, comment: string) =>
    api.post<RequestDetail>(`/api/requests/${id}/return`, { comment }),
};

// ─── Tasks ───

export const taskApi = {
  list: (params?: { status?: string; assignedTo?: string; requestId?: string }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.assignedTo) query.set('assignedTo', params.assignedTo);
    if (params?.requestId) query.set('requestId', params.requestId);
    const qs = query.toString();
    return api.get<TaskListItem[]>(`/api/tasks${qs ? `?${qs}` : ''}`);
  },

  getById: (taskId: string) => api.get<TaskDetail>(`/api/tasks/${taskId}`),

  assign: (taskId: string, assignedTo: string, assignedToDisplayName: string) =>
    api.post(`/api/tasks/${taskId}/assign`, { assignedTo, assignedToDisplayName }),

  updateStatus: (taskId: string, status: string, evidenceNotes?: string) =>
    api.post(`/api/tasks/${taskId}/status`, { status, evidenceNotes }),

  reopen: (requestId: string, reason: string) =>
    api.post(`/api/requests/${requestId}/reopen`, { reason }),
};
